import React, { useState, useEffect, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const HeroSection = () => {
  const [currentCodeIndex, setCurrentCodeIndex] = useState(0);
  const [displayedCode, setDisplayedCode] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const codeSnippets = useMemo(() => [
    {
      language: 'React',
      code: `const Portfolio = () => {
  const [skills, setSkills] = useState([]);
  
  useEffect(() => {
    fetchSkills().then(setSkills);
  }, []);
  
  return (
    <div className="portfolio">
      {skills.map(skill => (
        <SkillCard key={skill.id} {...skill} />
      ))}
    </div>
  );
};`,
      output: 'Interactive React Components'
    },
    {
      language: 'JavaScript',
      code: `const optimizePerformance = (data) => {
  const memoized = new Map();
  
  return data.reduce((acc, item) => {
    if (memoized.has(item.id)) {
      return [...acc, memoized.get(item.id)];
    }
    
    const processed = processItem(item);
    memoized.set(item.id, processed);
    return [...acc, processed];
  }, []);
};`,
      output: 'Performance Optimization'
    },
    {
      language: 'CSS',
      code: `.interactive-card {
  transform: perspective(1000px) rotateY(0deg);
  transition: all 0.6s cubic-bezier(0.23, 1, 0.320, 1);
  
  &:hover {
    transform: perspective(1000px) rotateY(15deg);
    box-shadow: 0 25px 50px rgba(0,0,0,0.2);
  }
}`,
      output: '3D CSS Animations'
    }
  ], []);

  useEffect(() => {
    const currentSnippet = codeSnippets[currentCodeIndex];
    let index = 0;
    setDisplayedCode('');
    setIsTyping(true);

    const typeWriter = setInterval(() => {
      if (index < currentSnippet.code.length) {
        setDisplayedCode(currentSnippet.code.slice(0, index + 1));
        index++;
      } else {
        clearInterval(typeWriter);
        setIsTyping(false);
        
        setTimeout(() => {
          setCurrentCodeIndex((prev) => (prev + 1) % codeSnippets.length);
        }, 3000);
      }
    }, 50);

    return () => clearInterval(typeWriter);
  }, [currentCodeIndex, codeSnippets]);

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-background via-muted to-background flex items-center justify-center overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, var(--color-accent) 2px, transparent 2px),
                           radial-gradient(circle at 75% 75%, var(--color-primary) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}></div>
      </div>
      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8 animate-fade-in-up">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-success rounded-full animate-pulse-soft"></div>
                <span className="text-sm font-medium text-text-secondary tracking-wide uppercase">
                  Available for Opportunities
                </span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-primary leading-tight">
                Crafting Digital
                <span className="block text-accent">Experiences</span>
              </h1>
              
              <p className="text-lg text-text-secondary leading-relaxed max-w-lg">
                Full-stack developer passionate about building scalable applications, 
                optimizing performance, and creating intuitive user experiences that solve real-world problems.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="FolderOpen"
                iconPosition="left"
                className="bg-accent hover:bg-hover text-accent-foreground shadow-medium hover:shadow-large"
              >
                View Projects
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                iconName="MessageCircle"
                iconPosition="left"
                className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
              >
                Let's Connect
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="flex items-center space-x-8 pt-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">50+</div>
                <div className="text-sm text-text-secondary">Projects</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">5+</div>
                <div className="text-sm text-text-secondary">Years</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-primary">15+</div>
                <div className="text-sm text-text-secondary">Technologies</div>
              </div>
            </div>
          </div>

          {/* Right Content - Live Code Editor */}
          <div className="relative animate-fade-in-up" style={{ animationDelay: '200ms' }}>
            <div className="bg-code-bg rounded-xl shadow-large overflow-hidden border border-border">
              {/* Editor Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-surface border-b border-border">
                <div className="flex items-center space-x-3">
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-error rounded-full"></div>
                    <div className="w-3 h-3 bg-warning rounded-full"></div>
                    <div className="w-3 h-3 bg-success rounded-full"></div>
                  </div>
                  <span className="text-sm font-medium text-text-secondary">
                    {codeSnippets[currentCodeIndex].language.toLowerCase()}.js
                  </span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Icon name="Play" size={16} className="text-success" />
                  <span className="text-xs text-text-secondary">Live Demo</span>
                </div>
              </div>

              {/* Code Content */}
              <div className="p-6 font-mono text-sm leading-relaxed code-scrollbar overflow-x-auto">
                <pre className="text-code-text">
                  <code>{displayedCode}</code>
                  {isTyping && (
                    <span className="inline-block w-2 h-5 bg-accent ml-1 animate-pulse"></span>
                  )}
                </pre>
              </div>

              {/* Output Preview */}
              <div className="px-6 py-4 bg-muted border-t border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="Terminal" size={16} className="text-accent" />
                    <span className="text-sm font-medium text-text-primary">Output:</span>
                  </div>
                  <div className="text-sm text-success font-medium">
                    {codeSnippets[currentCodeIndex].output}
                  </div>
                </div>
              </div>
            </div>

            {/* Code Language Indicators */}
            <div className="flex justify-center mt-6 space-x-2">
              {codeSnippets.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentCodeIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentCodeIndex 
                      ? 'bg-accent shadow-soft' 
                      : 'bg-border hover:bg-text-secondary'
                  }`}
                  aria-label={`View ${codeSnippets[index].language} code`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={24} className="text-text-secondary" />
      </div>
    </section>
  );
};

export default HeroSection;